import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Animated,
  Dimensions,
} from 'react-native';
import {
  Text,
  Card,
  Button,
  Chip,
  useTheme,
  IconButton,
  Switch,
  Divider,
} from 'react-native-paper';
import Slider from '@react-native-community/slider';
import {
  X,
  Filter,
  RotateCcw,
  TrendingUp,
  Clock,
  Target,
  Star,
  BookOpen,
  Coffee,
  Play,
  Brain,
} from 'lucide-react-native';
import type { ContentRecommendation } from '@coffeeplexity/shared';

interface RecommendationFiltersProps {
  filters: RecommendationFiltersState;
  onFiltersChange: (filters: Partial<RecommendationFiltersState>) => void;
  onClose: () => void;
  recommendations: ContentRecommendation[];
}

interface RecommendationFiltersState {
  contentTypes: string[];
  difficultyRange: [number, number];
  confidenceThreshold: number;
  skillCategories: string[];
  timeRange: [number, number];
  sortBy: 'confidence' | 'priority' | 'created_at' | 'difficulty';
  sortOrder: 'asc' | 'desc';
}

const { height: screenHeight } = Dimensions.get('window');

const CONTENT_TYPE_OPTIONS = [
  { id: 'recipe', label: 'Recipes', icon: Coffee, color: '#8B4513' },
  { id: 'video', label: 'Videos', icon: Play, color: '#FF6B35' },
  { id: 'article', label: 'Articles', icon: BookOpen, color: '#4ECDC4' },
  { id: 'quiz', label: 'Quizzes', icon: Brain, color: '#9B59B6' },
];

const SORT_OPTIONS = [
  { id: 'priority', label: 'Priority', icon: Star },
  { id: 'confidence', label: 'Confidence', icon: Target },
  { id: 'created_at', label: 'Newest', icon: Clock },
  { id: 'difficulty', label: 'Difficulty', icon: TrendingUp },
];

export function RecommendationFilters({
  filters,
  onFiltersChange,
  onClose,
  recommendations,
}: RecommendationFiltersProps) {
  const theme = useTheme();
  const [slideAnim] = useState(new Animated.Value(screenHeight));
  const [localFilters, setLocalFilters] = useState(filters);

  // Animation effect
  React.useEffect(() => {
    Animated.spring(slideAnim, {
      toValue: 0,
      useNativeDriver: true,
      tension: 100,
      friction: 8,
    }).start();
  }, [slideAnim]);

  // Extract available skill categories from recommendations
  const availableSkillCategories = useMemo(() => {
    const categories = new Set<string>();
    recommendations.forEach(rec => {
      rec.skillAlignment.forEach(skill => {
        categories.add(skill.skillName);
      });
    });
    return Array.from(categories).sort();
  }, [recommendations]);

  // Get filter statistics
  const filterStats = useMemo(() => {
    const totalRecommendations = recommendations.length;
    const filteredCount = recommendations.filter(rec => {
      // Apply current filters to get count
      if (
        localFilters.contentTypes.length > 0 &&
        !localFilters.contentTypes.includes(rec.contentType)
      ) {
        return false;
      }
      if (
        rec.difficultyLevel < localFilters.difficultyRange[0] ||
        rec.difficultyLevel > localFilters.difficultyRange[1]
      ) {
        return false;
      }
      if (rec.confidence < localFilters.confidenceThreshold) {
        return false;
      }
      if (
        rec.estimatedMinutes < localFilters.timeRange[0] ||
        rec.estimatedMinutes > localFilters.timeRange[1]
      ) {
        return false;
      }
      if (localFilters.skillCategories.length > 0) {
        const hasMatchingSkill = rec.skillAlignment.some(skill =>
          localFilters.skillCategories.includes(skill.skillName)
        );
        if (!hasMatchingSkill) {
          return false;
        }
      }
      return true;
    }).length;

    return {
      total: totalRecommendations,
      filtered: filteredCount,
      percentage:
        totalRecommendations > 0
          ? (filteredCount / totalRecommendations) * 100
          : 0,
    };
  }, [recommendations, localFilters]);

  // Handle close with animation
  const handleClose = useCallback(() => {
    Animated.timing(slideAnim, {
      toValue: screenHeight,
      duration: 300,
      useNativeDriver: true,
    }).start(() => {
      onClose();
    });
  }, [slideAnim, onClose]);

  // Handle apply filters
  const handleApplyFilters = useCallback(() => {
    onFiltersChange(localFilters);
    handleClose();
  }, [localFilters, onFiltersChange, handleClose]);

  // Handle reset filters
  const handleResetFilters = useCallback(() => {
    const resetFilters: RecommendationFiltersState = {
      contentTypes: [],
      difficultyRange: [0, 1],
      confidenceThreshold: 0.5,
      skillCategories: [],
      timeRange: [5, 120],
      sortBy: 'priority',
      sortOrder: 'desc',
    };
    setLocalFilters(resetFilters);
  }, []);

  // Handle content type toggle
  const handleContentTypeToggle = useCallback((contentType: string) => {
    setLocalFilters(prev => ({
      ...prev,
      contentTypes: prev.contentTypes.includes(contentType)
        ? prev.contentTypes.filter(type => type !== contentType)
        : [...prev.contentTypes, contentType],
    }));
  }, []);

  // Handle skill category toggle
  const handleSkillCategoryToggle = useCallback((skillCategory: string) => {
    setLocalFilters(prev => ({
      ...prev,
      skillCategories: prev.skillCategories.includes(skillCategory)
        ? prev.skillCategories.filter(cat => cat !== skillCategory)
        : [...prev.skillCategories, skillCategory],
    }));
  }, []);

  // Handle sort option change
  const handleSortChange = useCallback((sortBy: string) => {
    setLocalFilters(prev => ({
      ...prev,
      sortBy: sortBy as any,
    }));
  }, []);

  // Handle sort order toggle
  const handleSortOrderToggle = useCallback(() => {
    setLocalFilters(prev => ({
      ...prev,
      sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  // Format difficulty label
  const formatDifficultyLabel = (value: number) => {
    if (value <= 0.3) return 'Easy';
    if (value <= 0.7) return 'Medium';
    return 'Hard';
  };

  // Format time label
  const formatTimeLabel = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  };

  return (
    <Animated.View
      style={[
        styles.container,
        {
          backgroundColor: theme.colors.surface,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      {/* Header */}
      <View
        style={[styles.header, { borderBottomColor: theme.colors.outline }]}
      >
        <View style={styles.headerLeft}>
          <Filter size={20} color={theme.colors.primary} />
          <Text
            variant='titleMedium'
            style={[styles.title, { color: theme.colors.onSurface }]}
          >
            Filter Recommendations
          </Text>
        </View>
        <IconButton
          icon={X}
          size={20}
          onPress={handleClose}
          style={styles.closeButton}
        />
      </View>

      {/* Filter Stats */}
      <View
        style={[
          styles.statsContainer,
          { backgroundColor: theme.colors.surfaceVariant },
        ]}
      >
        <Text
          variant='bodyMedium'
          style={[styles.statsText, { color: theme.colors.onSurfaceVariant }]}
        >
          Showing {filterStats.filtered} of {filterStats.total} recommendations
          ({Math.round(filterStats.percentage)}%)
        </Text>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Content Types */}
        <Card style={styles.section}>
          <Card.Content>
            <Text
              variant='titleSmall'
              style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
            >
              Content Types
            </Text>
            <View style={styles.chipContainer}>
              {CONTENT_TYPE_OPTIONS.map(option => {
                const Icon = option.icon;
                const isSelected = localFilters.contentTypes.includes(
                  option.id
                );
                return (
                  <Chip
                    key={option.id}
                    mode={isSelected ? 'flat' : 'outlined'}
                    selected={isSelected}
                    onPress={() => handleContentTypeToggle(option.id)}
                    style={[
                      styles.chip,
                      isSelected && { backgroundColor: option.color + '20' },
                    ]}
                    textStyle={[
                      styles.chipText,
                      isSelected && { color: option.color },
                    ]}
                    icon={() => (
                      <Icon
                        size={14}
                        color={
                          isSelected
                            ? option.color
                            : theme.colors.onSurfaceVariant
                        }
                      />
                    )}
                  >
                    {option.label}
                  </Chip>
                );
              })}
            </View>
          </Card.Content>
        </Card>

        {/* Difficulty Range */}
        <Card style={styles.section}>
          <Card.Content>
            <Text
              variant='titleSmall'
              style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
            >
              Difficulty Level
            </Text>
            <View style={styles.sliderContainer}>
              <View style={styles.sliderLabels}>
                <Text
                  variant='bodySmall'
                  style={[
                    styles.sliderLabel,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  {formatDifficultyLabel(localFilters.difficultyRange[0])}
                </Text>
                <Text
                  variant='bodySmall'
                  style={[
                    styles.sliderLabel,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  {formatDifficultyLabel(localFilters.difficultyRange[1])}
                </Text>
              </View>
              {/* Note: React Native Paper doesn't have a range slider, so we'll use two separate sliders */}
              <View style={styles.rangeSliders}>
                <View style={styles.sliderRow}>
                  <Text
                    variant='bodySmall'
                    style={[
                      styles.sliderRowLabel,
                      { color: theme.colors.onSurfaceVariant },
                    ]}
                  >
                    Min:
                  </Text>
                  <Slider
                    style={styles.slider}
                    value={localFilters.difficultyRange[0]}
                    onValueChange={value =>
                      setLocalFilters(prev => ({
                        ...prev,
                        difficultyRange: [value, prev.difficultyRange[1]],
                      }))
                    }
                    minimumValue={0}
                    maximumValue={1}
                    step={0.1}
                    minimumTrackTintColor={theme.colors.primary}
                    maximumTrackTintColor={theme.colors.outline}
                    thumbTintColor={theme.colors.primary}
                  />
                </View>
                <View style={styles.sliderRow}>
                  <Text
                    variant='bodySmall'
                    style={[
                      styles.sliderRowLabel,
                      { color: theme.colors.onSurfaceVariant },
                    ]}
                  >
                    Max:
                  </Text>
                  <Slider
                    style={styles.slider}
                    value={localFilters.difficultyRange[1]}
                    onValueChange={value =>
                      setLocalFilters(prev => ({
                        ...prev,
                        difficultyRange: [prev.difficultyRange[0], value],
                      }))
                    }
                    minimumValue={0}
                    maximumValue={1}
                    step={0.1}
                    minimumTrackTintColor={theme.colors.primary}
                    maximumTrackTintColor={theme.colors.outline}
                    thumbTintColor={theme.colors.primary}
                  />
                </View>
              </View>
            </View>
          </Card.Content>
        </Card>

        {/* Confidence Threshold */}
        <Card style={styles.section}>
          <Card.Content>
            <Text
              variant='titleSmall'
              style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
            >
              Minimum Confidence:{' '}
              {Math.round(localFilters.confidenceThreshold * 100)}%
            </Text>
            <Slider
              style={styles.fullSlider}
              value={localFilters.confidenceThreshold}
              onValueChange={value =>
                setLocalFilters(prev => ({
                  ...prev,
                  confidenceThreshold: value,
                }))
              }
              minimumValue={0}
              maximumValue={1}
              step={0.05}
              minimumTrackTintColor={theme.colors.primary}
              maximumTrackTintColor={theme.colors.outline}
              thumbTintColor={theme.colors.primary}
            />
          </Card.Content>
        </Card>

        {/* Time Range */}
        <Card style={styles.section}>
          <Card.Content>
            <Text
              variant='titleSmall'
              style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
            >
              Time Duration
            </Text>
            <View style={styles.sliderContainer}>
              <View style={styles.sliderLabels}>
                <Text
                  variant='bodySmall'
                  style={[
                    styles.sliderLabel,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  {formatTimeLabel(localFilters.timeRange[0])}
                </Text>
                <Text
                  variant='bodySmall'
                  style={[
                    styles.sliderLabel,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  {formatTimeLabel(localFilters.timeRange[1])}
                </Text>
              </View>
              <View style={styles.rangeSliders}>
                <View style={styles.sliderRow}>
                  <Text
                    variant='bodySmall'
                    style={[
                      styles.sliderRowLabel,
                      { color: theme.colors.onSurfaceVariant },
                    ]}
                  >
                    Min:
                  </Text>
                  <Slider
                    style={styles.slider}
                    value={localFilters.timeRange[0]}
                    onValueChange={value =>
                      setLocalFilters(prev => ({
                        ...prev,
                        timeRange: [value, prev.timeRange[1]],
                      }))
                    }
                    minimumValue={5}
                    maximumValue={120}
                    step={5}
                    minimumTrackTintColor={theme.colors.primary}
                    maximumTrackTintColor={theme.colors.outline}
                    thumbTintColor={theme.colors.primary}
                  />
                </View>
                <View style={styles.sliderRow}>
                  <Text
                    variant='bodySmall'
                    style={[
                      styles.sliderRowLabel,
                      { color: theme.colors.onSurfaceVariant },
                    ]}
                  >
                    Max:
                  </Text>
                  <Slider
                    style={styles.slider}
                    value={localFilters.timeRange[1]}
                    onValueChange={value =>
                      setLocalFilters(prev => ({
                        ...prev,
                        timeRange: [prev.timeRange[0], value],
                      }))
                    }
                    minimumValue={5}
                    maximumValue={120}
                    step={5}
                    minimumTrackTintColor={theme.colors.primary}
                    maximumTrackTintColor={theme.colors.outline}
                    thumbTintColor={theme.colors.primary}
                  />
                </View>
              </View>
            </View>
          </Card.Content>
        </Card>

        {/* Skill Categories */}
        {availableSkillCategories.length > 0 && (
          <Card style={styles.section}>
            <Card.Content>
              <Text
                variant='titleSmall'
                style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
              >
                Skill Categories
              </Text>
              <View style={styles.chipContainer}>
                {availableSkillCategories.map(category => {
                  const isSelected =
                    localFilters.skillCategories.includes(category);
                  return (
                    <Chip
                      key={category}
                      mode={isSelected ? 'flat' : 'outlined'}
                      selected={isSelected}
                      onPress={() => handleSkillCategoryToggle(category)}
                      style={[
                        styles.chip,
                        isSelected && {
                          backgroundColor: theme.colors.primaryContainer,
                        },
                      ]}
                      textStyle={[
                        styles.chipText,
                        isSelected && {
                          color: theme.colors.onPrimaryContainer,
                        },
                      ]}
                    >
                      {category}
                    </Chip>
                  );
                })}
              </View>
            </Card.Content>
          </Card>
        )}

        {/* Sort Options */}
        <Card style={styles.section}>
          <Card.Content>
            <Text
              variant='titleSmall'
              style={[styles.sectionTitle, { color: theme.colors.onSurface }]}
            >
              Sort By
            </Text>
            <View style={styles.chipContainer}>
              {SORT_OPTIONS.map(option => {
                const Icon = option.icon;
                const isSelected = localFilters.sortBy === option.id;
                return (
                  <Chip
                    key={option.id}
                    mode={isSelected ? 'flat' : 'outlined'}
                    selected={isSelected}
                    onPress={() => handleSortChange(option.id)}
                    style={[
                      styles.chip,
                      isSelected && {
                        backgroundColor: theme.colors.primaryContainer,
                      },
                    ]}
                    textStyle={[
                      styles.chipText,
                      isSelected && { color: theme.colors.onPrimaryContainer },
                    ]}
                    icon={() => (
                      <Icon
                        size={14}
                        color={
                          isSelected
                            ? theme.colors.onPrimaryContainer
                            : theme.colors.onSurfaceVariant
                        }
                      />
                    )}
                  >
                    {option.label}
                  </Chip>
                );
              })}
            </View>

            <View style={styles.sortOrderContainer}>
              <Text
                variant='bodyMedium'
                style={[
                  styles.sortOrderLabel,
                  { color: theme.colors.onSurface },
                ]}
              >
                Sort Order:
              </Text>
              <View style={styles.sortOrderToggle}>
                <Text
                  variant='bodySmall'
                  style={[
                    styles.sortOrderText,
                    {
                      color:
                        localFilters.sortOrder === 'desc'
                          ? theme.colors.primary
                          : theme.colors.onSurfaceVariant,
                    },
                  ]}
                >
                  High to Low
                </Text>
                <Switch
                  value={localFilters.sortOrder === 'asc'}
                  onValueChange={handleSortOrderToggle}
                  color={theme.colors.primary}
                />
                <Text
                  variant='bodySmall'
                  style={[
                    styles.sortOrderText,
                    {
                      color:
                        localFilters.sortOrder === 'asc'
                          ? theme.colors.primary
                          : theme.colors.onSurfaceVariant,
                    },
                  ]}
                >
                  Low to High
                </Text>
              </View>
            </View>
          </Card.Content>
        </Card>
      </ScrollView>

      {/* Footer Actions */}
      <View style={[styles.footer, { borderTopColor: theme.colors.outline }]}>
        <Button
          mode='outlined'
          onPress={handleResetFilters}
          style={styles.footerButton}
          icon={RotateCcw}
        >
          Reset
        </Button>
        <Button
          mode='contained'
          onPress={handleApplyFilters}
          style={[styles.footerButton, styles.applyButton]}
        >
          Apply Filters
        </Button>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    fontWeight: '600',
  },
  closeButton: {
    margin: 0,
  },
  statsContainer: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  statsText: {
    fontSize: 12,
    fontWeight: '500',
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
  },
  section: {
    marginVertical: 8,
  },
  sectionTitle: {
    fontWeight: '600',
    marginBottom: 12,
  },
  chipContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    marginBottom: 4,
  },
  chipText: {
    fontSize: 12,
  },
  sliderContainer: {
    marginTop: 8,
  },
  sliderLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  sliderLabel: {
    fontSize: 12,
    fontWeight: '500',
  },
  rangeSliders: {
    gap: 8,
  },
  sliderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  sliderRowLabel: {
    fontSize: 12,
    minWidth: 30,
  },
  slider: {
    flex: 1,
    height: 20,
  },
  fullSlider: {
    width: '100%',
    height: 20,
    marginTop: 8,
  },
  sortOrderContainer: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },
  sortOrderLabel: {
    marginBottom: 8,
    fontWeight: '500',
  },
  sortOrderToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sortOrderText: {
    fontSize: 12,
    fontWeight: '500',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    gap: 12,
  },
  footerButton: {
    flex: 1,
  },
  applyButton: {
    backgroundColor: '#4ECDC4',
  },
});
